// ── engineCalculator.js ──────────────────────────────────────────────────────
// Mirror of: backend/resultsEngine/calculator.js
// Uses camelCase field names matching the ENGINE_BOOTSTRAP wire format.
// Keep logic in sync with backend when changing calculation rules.

import { formatTime, formatDiff } from './engineFormatter';

// ── Helpers ───────────────────────────────────────────────────────────────────

const isOdcinkowa    = (t) => (t || '').toLowerCase() === 'odcinkowa';
const isNieodcinkowa = (t) => (t || '').toLowerCase() === 'nieodcinkowa';

// Sum of all Odcinkowa penalties for a specific stage (can be multiple rows).
function getOdcinkowaPenaltySec(entryListId, stageId, penalties) {
  return (penalties.get(entryListId) || [])
    .filter(p => p.stageId === stageId && isOdcinkowa(p.penaltyType))
    .reduce((s, p) => s + Number(p.penaltySec), 0);
}

// Sum of all Odcinkowa penalties across all stages up to upToStageOrder.
function getAllOdcinkowaPenaltySecUpTo(entryListId, upToStageOrder, data) {
  return (data.penalties.get(entryListId) || [])
    .filter(p => {
      if (!isOdcinkowa(p.penaltyType)) return false;
      const ps = data.stages.get(p.stageId);
      return ps && ps.stageOrder <= upToStageOrder;
    })
    .reduce((s, p) => s + Number(p.penaltySec), 0);
}

function getNieodcinkowaPenaltySec(entryListId, upToStageOrder, data) {
  return (data.penalties.get(entryListId) || [])
    .filter(p => {
      if (!isNieodcinkowa(p.penaltyType)) return false;
      const ps = data.stages.get(p.stageId);
      return ps && ps.stageOrder <= upToStageOrder;
    })
    .reduce((s, p) => s + Number(p.penaltySec), 0);
}

function getStageTimeSec(entryListId, stageId, classificationId, data) {
  const byClass = data.times.get(stageId) && data.times.get(stageId).get(entryListId);
  if (!byClass) return null;
  const record = byClass.has(classificationId) ? byClass.get(classificationId) : byClass.get(null);
  if (!record || record.timeSec == null) return null;
  return record.timeSec + getOdcinkowaPenaltySec(entryListId, stageId, data.penalties);
}

function stageHasAnyResult(stageId, data) {
  return (data.times.get(stageId) || { size: 0 }).size > 0;
}

function getCumulativeTimeSec(entryListId, upToStageId, classificationId, data) {
  const upToStage = data.stages.get(upToStageId);
  if (!upToStage) return null;
  const upToOrder = upToStage.stageOrder;
  const compId    = upToStage.competitionId;
  let total = 0;

  for (const stageId of data.stagesSorted) {
    const stage = data.stages.get(stageId);
    if (stage.stageOrder > upToOrder) break;
    if (stage.competitionId !== compId) continue;
    if (!stage.isClassified) continue;
    if (stage.canceled) continue;
    if (!stageHasAnyResult(stageId, data)) continue;

    const t = getStageTimeSec(entryListId, stageId, classificationId, data);
    if (t == null) return null;
    total += t;
  }

  return total + getNieodcinkowaPenaltySec(entryListId, upToOrder, data);
}

// ── Core ranking builder ──────────────────────────────────────────────────────

function buildRanking(classificationId, getTotalFn, precision, data, groupId, includeAsOut) {
  const classEntries = data.classEntries.get(classificationId);
  if (!classEntries) return [];

  const defaultIncludeAsOut = (id, entry, ce) => !ce.active || (entry && entry.active === 0);
  const shouldBeOut = includeAsOut !== undefined ? includeAsOut : defaultIncludeAsOut;

  const rows = [];
  for (const [entryListId, ce] of classEntries) {
    if (groupId != null && (ce.inGroup || '').trim() !== groupId) continue;
    const entry = data.entries.get(entryListId);
    const totalTimeSec = getTotalFn(entryListId);

    if (totalTimeSec == null) {
      if (shouldBeOut(entryListId, entry, ce)) {
        rows.push({
          _entryListId:  entryListId,
          positionNumber: entry ? entry.positionNumber : undefined,
          driverName:    ce.driverName   || '',
          coDriverName:  ce.coDriverName || '',
          car:           ce.car          || '',
          inGroup:       ce.inGroup      || '',
          competitor:    ce.competitor   || '',
          _totalTimeSec: null,
        });
      }
    } else {
      rows.push({
        _entryListId:  entryListId,
        positionNumber: entry ? entry.positionNumber : undefined,
        driverName:    ce.driverName   || '',
        coDriverName:  ce.coDriverName || '',
        car:           ce.car          || '',
        inGroup:       ce.inGroup      || '',
        competitor:    ce.competitor   || '',
        _totalTimeSec: totalTimeSec,
      });
    }
  }

  const finished = rows
    .filter(r => r._totalTimeSec != null)
    .sort((a, b) => a._totalTimeSec - b._totalTimeSec);
  const outs = rows.filter(r => r._totalTimeSec == null);

  let next = 1;
  for (let i = 0; i < finished.length; i++) {
    const sameTime = i > 0 && finished[i]._totalTimeSec === finished[i - 1]._totalTimeSec;
    finished[i].rowNumber = sameTime ? finished[i - 1].rowNumber : next;
    next++;
  }

  // Class positions — per inGroup
  const groupState = new Map();
  for (const row of finished) {
    const g = row.inGroup || '';
    if (!groupState.has(g)) groupState.set(g, { nextPos: 1, prevPos: 0, prevTime: null });
    const s = groupState.get(g);
    const sameTime = s.prevTime !== null && row._totalTimeSec === s.prevTime;
    row.classRowNumber = sameTime ? s.prevPos : s.nextPos;
    if (!sameTime) s.prevPos = s.nextPos;
    s.nextPos++;
    s.prevTime = row._totalTimeSec;
  }
  for (const row of outs) {
    row.classRowNumber = 'OUT';
  }

  const leader = finished[0];
  let prev = null;
  for (const row of finished) {
    if (!leader) {
      row._diffToFirst = null;
      row._diffToPrev  = null;
    } else {
      row._diffToFirst = row._totalTimeSec - leader._totalTimeSec;
      row._diffToPrev  = prev ? row._totalTimeSec - prev._totalTimeSec : null;
      prev = row;
    }
  }

  const fmt = r => ({
    ...r,
    diffToFirst: formatDiff(r._diffToFirst, precision),
    diffToPrev:  formatDiff(r._diffToPrev,  precision),
  });

  return [
    ...finished.map(fmt),
    ...outs.map(r => ({ ...r, rowNumber: 'OUT', diffToFirst: '--', diffToPrev: '--' })),
  ];
}

// ── Public functions ──────────────────────────────────────────────────────────

export function buildStageRanking(stageId, classificationId, groupId, data) {
  const stage = data.stages.get(stageId);
  if (!stage) return [];
  const precision = stage.precision;

  const rows = buildRanking(
    classificationId,
    id => getStageTimeSec(id, stageId, classificationId, data),
    precision, data, groupId
  );

  const leaderTimeSec = rows.length && rows[0]._totalTimeSec != null ? rows[0]._totalTimeSec : null;
  const leaderSpeed   = leaderTimeSec && stage.distance
    ? stage.distance / (leaderTimeSec / 3600) : null;

  return rows.map(row => {
    const penaltySec = getOdcinkowaPenaltySec(row._entryListId, stageId, data.penalties);
    const speed = row._totalTimeSec && stage.distance
      ? Math.round((stage.distance / (row._totalTimeSec / 3600)) * 100) / 100
      : null;
    const byClass = data.times.get(stageId) && data.times.get(stageId).get(row._entryListId);
    const taryfa  = ((byClass && (byClass.has(classificationId) ? byClass.get(classificationId) : byClass.get(null))) || {}).taryfa || false;

    return {
      rowNumber:       row.rowNumber,
      positionNumber:  row.positionNumber,
      driverName:      row.driverName,
      coDriverName:    row.coDriverName,
      car:             row.car,
      inGroup:         row.inGroup,
      classPosition:   row.classRowNumber,
      resultTime:      formatTime(row._totalTimeSec, precision),
      penalty:         penaltySec > 0 ? formatTime(penaltySec, precision) : null,
      diffToFirst:     row.diffToFirst,
      diffToPrev:      row.diffToPrev,
      taryfa,
      avgSpeedKmh:     speed,
      speedDeficitKmh: speed != null && leaderSpeed != null && row.rowNumber !== 1
        ? Math.round((leaderSpeed - speed) * 10) / 10
        : null,
    };
  });
}

export function buildCumulativeRanking(stageId, classificationId, groupId, data, prevRanking) {
  if (prevRanking === undefined) prevRanking = null;
  const stage = data.stages.get(stageId);
  if (!stage) return [];
  const precision = stage.precision;
  const upToOrder = stage.stageOrder;

  const rows = buildRanking(
    classificationId,
    id => getCumulativeTimeSec(id, stageId, classificationId, data),
    precision, data, groupId,
    (id, entry) => entry && entry.active === 0
  );

  const prevMap = prevRanking
    ? new Map(
        prevRanking
          .filter(r => r.rowNumber !== 'OUT' && typeof r.rowNumber === 'number')
          .map(r => [r.positionNumber, r.rowNumber])
      )
    : null;

  const prevClassMap = prevRanking
    ? new Map(
        prevRanking
          .filter(r => typeof r.classPosition === 'number')
          .map(r => [r.positionNumber, r.classPosition])
      )
    : null;

  return rows.map(row => {
    const allPenaltySec =
      getAllOdcinkowaPenaltySecUpTo(row._entryListId, upToOrder, data) +
      getNieodcinkowaPenaltySec(row._entryListId, upToOrder, data);

    const change = prevMap && typeof row.rowNumber === 'number'
      ? (prevMap.has(row.positionNumber) ? prevMap.get(row.positionNumber) : null)
      : null;

    const classChange = prevClassMap && typeof row.classRowNumber === 'number'
      ? (prevClassMap.has(row.positionNumber) ? prevClassMap.get(row.positionNumber) - row.classRowNumber : null)
      : null;

    return {
      rowNumber:           row.rowNumber,
      positionNumber:      row.positionNumber,
      driverName:          row.driverName,
      coDriverName:        row.coDriverName,
      car:                 row.car,
      inGroup:             row.inGroup,
      classPosition:       row.classRowNumber,
      totalTime:           formatTime(row._totalTimeSec, precision),
      penalty:             allPenaltySec > 0 ? formatTime(allPenaltySec, precision) : null,
      diffToFirst:         row.diffToFirst,
      diffToPrev:          row.diffToPrev,
      positionChange:      change !== null ? (change - row.rowNumber) : null,
      classPositionChange: classChange,
    };
  });
}

export function buildTestStageRuns(stageId, classificationId, data) {
  const stage = data.stages.get(stageId);
  if (!stage || !stage.isTest) return [];
  const precision = stage.precision;

  const classEntries    = data.classEntries.get(classificationId);
  const officialByEntry = data.times.get(stageId);

  // Build one group per crew: { sortKey, rows[] } where each row = one run
  const crews = [];

  for (const [entryListId, ce] of (classEntries || [])) {
    if (!ce.active) continue;
    const entry  = data.entries.get(entryListId);
    const runs   = data.testRuns.get(stageId)?.get(entryListId) || [];
    const valid  = runs.filter(r => r.timeSec != null);
    const bestSec = valid.length ? Math.min(...valid.map(r => r.timeSec)) : null;

    // Official time from TIME_RESULT (set by operator) overrides auto-best for sorting
    const byClass  = officialByEntry?.get(entryListId);
    const official = (byClass?.has(classificationId)
      ? byClass.get(classificationId)
      : byClass?.get(null))?.timeSec ?? null;

    if (valid.length === 0 && official == null) continue;

    const sortKey = official ?? bestSec;

    const base = {
      positionNumber: entry?.positionNumber,
      driverName:    ce.driverName   || '',
      coDriverName:  ce.coDriverName || '',
      car:           ce.car          || '',
      inGroup:       ce.inGroup      || '',
    };

    const distanceKm = stage.distance ?? null;

    // One flat row per run (keeps run order); empty row if no runs yet
    const rows = runs.length
      ? runs.map(r => ({
          ...base,
          resultTime:       formatTime(r.timeSec, precision),
          _timeSec:         r.timeSec ?? null,
          _diffFromBestSec: (r.timeSec != null && bestSec != null) ? r.timeSec - bestSec : null,
          _distanceKm:      distanceKm,
        }))
      : [{ ...base, resultTime: null, _timeSec: null, _diffFromBestSec: null, _distanceKm: distanceKm }];

    crews.push({ sortKey, rows });
  }

  // Sort crews by best/official time (null → last)
  crews.sort((a, b) => {
    if (a.sortKey == null) return 1;
    if (b.sortKey == null) return -1;
    return a.sortKey - b.sortKey;
  });

  // Flatten: all runs for crew 1, then all runs for crew 2, …
  return crews.flatMap(c => c.rows);
}
